import React, { memo, lazy } from "react";
import { graphql } from "gatsby";
import classNames from "classnames";

import StaticQueriesWrapper from "./StaticQueriesWrapper";
import {
    dynamic as dynamicMap,
    local as staticList,
} from "../utils/pageComponents";

// Lazy-loaded Section wrapper
const Section = lazy(() => import("./Section"));

// Cache for dynamically imported modules
const componentCache = {};

/**
 * Dynamically import a component by its file name, caching the result.
 * @param {string} fileName - Component file name (without extension).
 * @returns {React.LazyExoticComponent} Lazy-loaded component.
 */
const getLazyComponent = fileName => {
    if (!fileName) return null;
    if (!componentCache[fileName]) {
        componentCache[fileName] = lazy(() => import(`./${fileName}.js`));
    }
    return componentCache[fileName];
};

/**
 * Render dynamic Contentful components based on a mapping of content type IDs.
 *
 * Props:
 * - contentfulComponents: array of Contentful component data
 * - other props are passed through to each component
 */
const DynamicChildren = ({ contentfulComponents = [], ...props }) => {
    // Nothing to render
    if (contentfulComponents.length === 0) return null;

    return contentfulComponents.map((item, idx) => {
        const {
            sys: {
                contentType: {
                    sys: { id: contentTypeId },
                },
            },
            component: staticName,
            takesFullPageWidth,
        } = item;

        // Determine file name: static vs dynamic mapping
        const fileName =
            contentTypeId === "staticComponent"
                ? staticList.includes(staticName)
                    ? staticName
                    : null
                : dynamicMap[contentTypeId];

        if (!fileName) return null;

        const Component = getLazyComponent(fileName);
        const key = `${contentTypeId}-${idx}`;

        return (
            <StaticQueriesWrapper key={key}>
                    <Section
                        className={classNames({
                            "my-0":
                                staticName === "ShareButtons" ||
                                takesFullPageWidth,
                        })}
                        noContainer={takesFullPageWidth}
                    >
                        <Component {...item} {...props} />
                    </Section>
            </StaticQueriesWrapper>
        );
    });
};

// GraphQL fragment to fetch dynamic components
export const query = graphql`
    fragment DynamicComponents on ContentfulPage {
        dynamicComponents {
            ...DynamicCards
            ...DynamicTestimonials
            ...DynamicDigitalCarousel
            ...DynamicRow
            ...DynamicIconTiles
            ...DynamicColumn
            ... on ContentfulStaticComponent {
                sys {
                    contentType {
                        sys {
                            id
                        }
                    }
                }
                name
                component
            }
        }
    }
`;

export default memo(DynamicChildren);
